import { useState } from 'react';
import type { RenderCellProps } from 'components/DataGrid';
import type { Calendar, Segment, SegmentHandle, SegmentSelectionStateType, SelectionState } from './types';
import type { GridRow } from '../../types';
import invariant from 'tiny-invariant';
import {
  computeWorkUnitDelta,
  getPositionError,
  getSegmentMouseHit,
  isAligned,
  shiftIntervalByWorkUnits,
} from './calc';
import { UNDERSIZED_WIDTH } from './const';
import { formatISO, max, min } from 'date-fns';
import { useRowSegments } from './useRowSegments';
import type { PlanRow } from 'modules/campaign/row';
import { PlanMutations, usePlanMutations } from '../../state/mutate';
import { openPositionDialog } from './PositionDialog';

type UseMouseDownHandlerProps = RenderCellProps<GridRow, any> & { calendar: Calendar };

type DragEventData = {
  x: number;
  day: Date;
  ctrlKey: boolean;
};

type DragContext = {
  initialX: number;
  initialDay: Date;
  ctrlKey: boolean;
};

type DragHandler = {
  onMove: (data: DragEventData, ctx: DragContext) => void;
  onEnd: (didMove: boolean, ctx: DragContext) => Promise<void>;
};

// no useCallback since bind it to <div>
export function useMouseDownHandler(props: UseMouseDownHandlerProps) {
  const {
    column: { key },
    row,
    calendar,
  } = props;
  invariant(row.kind !== 'SMART');

  const segments = useRowSegments(row.positions, calendar);
  const [selection, setSelection] = useState<SelectionState>(null);
  const { selectCell, update, requestHandler } = usePlanMutations();

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.buttons !== 1) return;
    selectCell(row._key, key, false, false);
    event.stopPropagation();
    event.preventDefault();

    // We just want to get to dialog wihhout selection
    if (event.shiftKey) {
      openPositionDialog(row, calendar.interval, calendar.resolution, update, requestHandler, null);
      return;
    }

    const rowLeft = event.currentTarget.getBoundingClientRect().left;

    const toEventData = (e: MouseEvent): DragEventData => {
      const x = e.clientX - rowLeft;
      return { x, day: calendar.pixelToDate(x), ctrlKey: e.ctrlKey };
    };

    const initial = toEventData(event.nativeEvent);
    const ctx: DragContext = { initialX: initial.x, initialDay: initial.day, ctrlKey: initial.ctrlKey };

    const segmentHit = getSegmentMouseHit(ctx.initialX, segments);

    const { onMove, onEnd } = segmentHit
      ? startSegmentDrag(segmentHit, segments, calendar, setSelection, update, requestHandler, row)
      : startSelectionDrag(ctx, segments, calendar, setSelection, update, requestHandler, row);

    let didMove = false;

    function onMouseMove(e: MouseEvent): void {
      // this is here just so we can figure out if it's onClick/onDragend
      // if we ever care if move actually happened we would compare delta in onEnd
      didMove = true;
      onMove(toEventData(e), ctx);
    }

    async function onMouseUp(): Promise<void> {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      try {
        await onEnd(didMove, ctx);
      } finally {
        setSelection(null);
      }
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return { selection, handleMouseDown, segments };
}

function startSegmentDrag(
  hit: [segment: Segment, handle: SegmentHandle],
  segments: Segment[],
  calendar: Calendar,
  setSelection: (s: SelectionState) => void,
  update: PlanMutations['update'],
  requestHandler: PlanMutations['requestHandler'],
  row: PlanRow,
): DragHandler {
  const [segment, handle] = hit;

  let lastSelection: SegmentSelectionStateType = {
    type: 'segment',
    handle,
    error: getPositionError(segments, { start: segment.start, end: segment.end }, segment.id),
    ...segment,
  };
  setSelection(lastSelection);

  return {
    onMove({ day, ctrlKey }, { initialDay }) {
      const effectiveResolution = !ctrlKey && segment.aligned ? calendar.resolution : 'DAY';

      const unitDelta = computeWorkUnitDelta(initialDay, day, effectiveResolution);
      const newInterval = shiftIntervalByWorkUnits(
        { start: segment.start, end: segment.end },
        handle,
        unitDelta,
        effectiveResolution,
        calendar.interval,
      );

      if (
        newInterval.start.getTime() === lastSelection.start.getTime() &&
        newInterval.end.getTime() === lastSelection.end.getTime()
      )
        return;

      const bounds = calendar.intervalToBounds(newInterval);
      const error = getPositionError(segments, newInterval, segment.id);

      lastSelection = {
        type: 'segment',
        handle,
        error,
        id: segment.id,
        quantity: segment.quantity,
        date_from: formatISO(newInterval.start, { representation: 'date' }),
        date_to: formatISO(newInterval.end, { representation: 'date' }),
        start: newInterval.start,
        end: newInterval.end,
        ...bounds,
        undersized: bounds.width < UNDERSIZED_WIDTH,
        aligned: isAligned(newInterval.start, newInterval.end, calendar.resolution),
      };
      setSelection(lastSelection);
    },

    async onEnd(didMove) {
      if (lastSelection.error) {
        setSelection(null);
        return;
      }

      if (didMove) {
        const res = await update({
          rows: {
            upsert: [
              { id: row.id, positions: row.positions.map((p) => (p.id === lastSelection.id ? lastSelection : p)) },
            ],
          },
        });
        res.unwrap();
      } else {
        await openPositionDialog(row, calendar.interval, calendar.resolution, update, requestHandler, lastSelection);
      }
      setSelection(null);
    },
  };
}

function startSelectionDrag(
  ctx: DragContext,
  segments: Segment[],
  calendar: Calendar,
  setSelection: (s: SelectionState) => void,
  update: PlanMutations['update'],
  requestHandler: PlanMutations['requestHandler'],
  row: PlanRow,
): DragHandler {
  const initInterval = ctx.ctrlKey
    ? { start: ctx.initialDay, end: ctx.initialDay }
    : calendar.dateToInterval(ctx.initialDay);
  let start: Date = initInterval.start;
  let end: Date = initInterval.end;

  let error = getPositionError(segments, { start, end });
  setSelection({ type: 'selection', start, end, error });

  return {
    onMove({ day, ctrlKey }, { initialDay }) {
      let newStart: Date, newEnd: Date;
      if (ctrlKey) {
        newStart = min([initialDay, day]);
        newEnd = max([initialDay, day]);
      } else {
        const initIv = calendar.dateToInterval(initialDay);
        const curIv = calendar.dateToInterval(day);
        newStart = min([initIv.start, curIv.start]);
        newEnd = max([initIv.end, curIv.end]);
      }
      if (newStart.getTime() === start.getTime() && newEnd.getTime() === end.getTime()) return;

      start = newStart;
      end = newEnd;
      error = getPositionError(segments, { start, end });
      setSelection({ type: 'selection', start, end, error });
    },

    async onEnd() {
      if (error) {
        setSelection(null);
        return;
      }

      await openPositionDialog(row, calendar.interval, calendar.resolution, update, requestHandler, {
        type: 'selection',
        start,
        end,
        error: null,
      });
      setSelection(null);
    },
  };
}
